import { useState } from 'react';
import { useUserContext } from '../context/UserContext';
import {
  User,
  Mail,
  Building2,
  Globe,
  Monitor,
  LogOut,
  ChevronRight,
  Info,
  Key,
} from 'lucide-react';

interface SettingSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

function SettingSection({ title, description, children }: SettingSectionProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">{title}</h2>
      {description && <p className="text-sm text-gray-500 mb-4">{description}</p>}
      {children}
    </div>
  );
}

interface SettingRowProps {
  icon: React.ReactNode;
  label: string;
  value?: string | React.ReactNode;
  description?: string;
  action?: React.ReactNode;
}

function SettingRow({ icon, label, value, description, action }: SettingRowProps) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          {icon}
        </div>
        <div>
          <p className="font-medium text-gray-900">{label}</p>
          {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
          {value && typeof value === 'string' ? (
            <p className="text-sm text-gray-600 mt-1">{value}</p>
          ) : (
            value
          )}
        </div>
      </div>
      {action && <div className="flex-shrink-0 ml-4">{action}</div>}
    </div>
  );
}

export function SettingsPage() {
  const { user, logout, authMethod, environment, brandId, isMAppContext } = useUserContext();

  const [notificationEmail, setNotificationEmail] = useState(true);

  const isVendor = user?.userType === 'vendor';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Profile section */}
      <SettingSection title="Profile" description="Your account information">
        <div className="space-y-1">
          <SettingRow
            icon={<User className="w-4 h-4 text-gray-600" />}
            label="Name"
            value={`${user?.firstName} ${user?.lastName || ''}`}
          />
          <SettingRow
            icon={<Mail className="w-4 h-4 text-gray-600" />}
            label="Email"
            value={user?.email}
          />
          {user?.yumId && <SettingRow
            icon={<Key className="w-4 h-4 text-gray-600" />}
            label="User ID"
            value={user?.yumId}
          />}
          <SettingRow
            icon={<Building2 className="w-4 h-4 text-gray-600" />}
            label="Account Type"
            value={
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium mt-1 ${
                  isVendor ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {isVendor ? (
                  <>
                    <Mail className="w-3 h-3" />
                    Vendor User
                  </>
                ) : (
                  <>
                    <Building2 className="w-3 h-3" />
                    Internal User
                  </>
                )}
              </span>
            }
          />
        </div>
      </SettingSection>

      {/* Notifications */}
      <SettingSection title="Notifications" description="How you receive updates">
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                <Mail className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Email Notifications</p>
                <p className="text-sm text-gray-500">Receive updates via email</p>
              </div>
            </div>
            <button
              onClick={() => setNotificationEmail(!notificationEmail)}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                notificationEmail ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  notificationEmail ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </SettingSection>

      {/* System Info */}
      <SettingSection title="System Information" description="Technical details about your session">
        <div className="space-y-1">
          <SettingRow
            icon={<Globe className="w-4 h-4 text-gray-600" />}
            label="Environment"
            value={
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium mt-1 ${
                  environment === 'prod'
                    ? 'bg-green-100 text-green-700'
                    : environment === 'staging'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                }`}
              >
                {environment || 'local'}
              </span>
            }
          />
          <SettingRow
            icon={<Monitor className="w-4 h-4 text-gray-600" />}
            label="Authentication Method"
            value={authMethod === 'email' ? 'Email/OTP' : 'MicroApp'}
          />
          {brandId && (
            <SettingRow
              icon={<Building2 className="w-4 h-4 text-gray-600" />}
              label="Brand Context"
              value={brandId}
            />
          )}
        </div>
      </SettingSection>

      {/* About */}
      <SettingSection title="About">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-byte-orange via-byte-crimson to-byte-purple flex items-center justify-center flex-shrink-0">
            <Info className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="font-medium text-gray-900">Byte Quality Complaints</p>
            <p className="text-sm text-gray-500 mt-0.5">
              Quality complaint management for stores and suppliers
            </p>
            <p className="text-xs text-gray-400 mt-2">Version 1.0.0</p>
          </div>
        </div>
      </SettingSection>

      {!isMAppContext && (
        <button
          onClick={logout}
          className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-red-200 hover:bg-red-50 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
              <LogOut className="w-4 h-4 text-red-600" />
            </div>
            <div className="text-left">
              <p className="font-medium text-red-600">Sign Out</p>
              <p className="text-sm text-gray-500">End your current session</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors" />
        </button>
      )}
    </div>
  );
}
